/**
 * Contact Form JavaScript
 * Handles phone input, file upload, and form submission
 */
(function() {
  'use strict';

  if (window.sugarContactFormsInitialized) return;
  window.sugarContactFormsInitialized = true;
  const initializedPhoneBlocks = new WeakSet();
  const initializedFileBlocks = new WeakSet();
  const initializedForms = new WeakSet();

  // Initialize all contact forms on the page
  function initContactForms() {
    const forms = document.querySelectorAll('[data-contact-form]');
    forms.forEach(initForm);
  }

  function initForm(formSection) {
    initPhoneInputs(formSection);
    initFileUploads(formSection);
    initFormSubmission(formSection);
  }

  // ===========================
  // Phone Input Functionality
  // ===========================
  function initPhoneInputs(container) {
    const phoneBlocks = container.querySelectorAll('[data-phone-block]');
    phoneBlocks.forEach(initPhoneBlock);
  }

  function initPhoneBlock(block) {
    if (initializedPhoneBlocks.has(block)) return;
    const defaultCountry = block.dataset.defaultCountry || 'US';
    const showFlags = block.dataset.showFlags !== 'false';
    const countryBtn = block.querySelector('[data-phone-country-btn]');
    const dropdown = block.querySelector('[data-phone-dropdown]');
    const searchInput = block.querySelector('[data-phone-search]');
    const list = block.querySelector('[data-phone-list]');
    const flagSpan = block.querySelector('[data-phone-flag]');
    const dialSpan = block.querySelector('[data-phone-dial]');
    const phoneInput = block.querySelector('[data-phone-input]');
    const combinedInput = block.querySelector('[data-phone-combined]');

    // Get phone codes data
    const codesScript = document.querySelector('[data-phone-codes]');
    if (!codesScript) return;

    let countries = [];
    try {
      countries = JSON.parse(codesScript.textContent);
    } catch (e) {
      console.error('Failed to parse phone codes:', e);
      return;
    }

    if (!countries.length) return;
    initializedPhoneBlocks.add(block);
    let selectedCountry = countries.find(c => c.code === defaultCountry) || countries[0];

    // Render country list
    function renderList(filter = '') {
      const filterLower = filter.toLowerCase();
      const filtered = filter
        ? countries.filter(c =>
            c.name.toLowerCase().includes(filterLower) ||
            c.dial.includes(filter) ||
            c.code.toLowerCase().includes(filterLower)
          )
        : countries;

      list.innerHTML = filtered.map(country => `
        <li class="contact-form__phone-option${country.code === selectedCountry.code ? ' is-selected' : ''}"
            data-country-code="${country.code}"
            role="option"
            aria-selected="${country.code === selectedCountry.code}">
          ${showFlags ? `<span class="contact-form__phone-flag">${country.flag}</span>` : ''}
          <span class="contact-form__phone-option-name">${country.name}</span>
          <span class="contact-form__phone-option-dial">${country.dial}</span>
        </li>
      `).join('');

      // Add click handlers and make focusable
      list.querySelectorAll('.contact-form__phone-option').forEach(option => {
        option.setAttribute('tabindex', '0');
        option.addEventListener('click', () => {
          const code = option.dataset.countryCode;
          selectedCountry = countries.find(c => c.code === code);
          updateDisplay();
          closeDropdown();
          phoneInput.focus();
        });
      });
    }

    // Update displayed country
    function updateDisplay() {
      flagSpan.textContent = showFlags ? selectedCountry.flag : '';
      flagSpan.style.display = showFlags ? '' : 'none';
      dialSpan.textContent = selectedCountry.dial;
      updateCombinedValue();
    }

    // Update hidden combined input
    function updateCombinedValue() {
      const phoneValue = phoneInput.value.trim();
      if (phoneValue) {
        combinedInput.value = selectedCountry.dial + ' ' + phoneValue;
      } else {
        combinedInput.value = '';
      }
    }

    // Toggle dropdown
    function openDropdown() {
      dropdown.classList.add('is-open');
      countryBtn.setAttribute('aria-expanded', 'true');
      searchInput.value = '';
      renderList();
      searchInput.focus();
    }

    function closeDropdown() {
      dropdown.classList.remove('is-open');
      countryBtn.setAttribute('aria-expanded', 'false');
    }

    function toggleDropdown() {
      if (dropdown.classList.contains('is-open')) {
        closeDropdown();
      } else {
        openDropdown();
      }
    }

    // Event listeners
    countryBtn.addEventListener('click', (e) => {
      e.preventDefault();
      toggleDropdown();
    });

    searchInput.addEventListener('input', () => {
      renderList(searchInput.value);
    });

    phoneInput.addEventListener('input', updateCombinedValue);


    // Keyboard navigation
    searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeDropdown();
        countryBtn.focus();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        const firstOption = list.querySelector('.contact-form__phone-option');
        if (firstOption) firstOption.focus();
      }
    });

    list.addEventListener('keydown', (e) => {
      const options = Array.from(list.querySelectorAll('.contact-form__phone-option'));
      const currentIndex = options.indexOf(document.activeElement);

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = options[currentIndex + 1];
        if (next) next.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (currentIndex === 0) {
          searchInput.focus();
        } else {
          const prev = options[currentIndex - 1];
          if (prev) prev.focus();
        }
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (document.activeElement.classList.contains('contact-form__phone-option')) {
          document.activeElement.click();
        }
      } else if (e.key === 'Escape') {
        closeDropdown();
        countryBtn.focus();
      }
    });

    // Initialize display
    updateDisplay();
    renderList();
  }

  // ===========================
  // File Upload Functionality
  // ===========================
  function initFileUploads(container) {
    const fileBlocks = container.querySelectorAll('[data-file-block]');
    fileBlocks.forEach(initFileBlock);
  }

  function initFileBlock(block) {
    if (initializedFileBlocks.has(block)) return;
    initializedFileBlocks.add(block);
    const cloudName = block.dataset.cloudName;
    const uploadPreset = block.dataset.uploadPreset;
    const acceptedTypes = block.dataset.acceptedTypes || '';
    const maxSize = parseInt(block.dataset.maxSize || '10', 10);

    const fileBtn = block.querySelector('[data-file-btn]');
    const fileWrapper = block.querySelector('[data-file-wrapper]');
    const fileStatus = block.querySelector('[data-file-status]');
    const fileRemove = block.querySelector('[data-file-remove]');
    const fileUrlInput = block.querySelector('[data-file-url]');
    const fileError = block.querySelector('[data-file-error]');

    if (!cloudName || !uploadPreset) {
      fileBtn.addEventListener('click', () => {
        showError('Cloudinary is not configured. Please set up Cloud name and Upload preset in section settings.');
      });
      return;
    }

    function showError(message) {
      fileError.textContent = message;
      fileError.classList.add('is-visible');
    }

    function hideError() {
      fileError.classList.remove('is-visible');
    }

    function setFile(fileName, url) {
      fileStatus.textContent = fileName;
      fileStatus.classList.add('has-file');
      fileWrapper.classList.add('has-file');
      fileUrlInput.value = url;
      hideError();
    }

    function clearFile() {
      // Check if classic style (has the inner button)
      const isClassic = block.querySelector('.contact-form__file-btn');
      fileStatus.textContent = isClassic ? 'No file selected' : '';
      fileStatus.classList.remove('has-file');
      fileWrapper.classList.remove('has-file');
      fileUrlInput.value = '';
      hideError();
    }

    function openCloudinaryWidget() {
      hideError();

      // Check if Cloudinary widget is available
      if (typeof cloudinary === 'undefined' || !cloudinary.createUploadWidget) {
        showError('Cloudinary widget is not loaded. Please check your configuration.');
        return;
      }

      // Parse accepted types for Cloudinary
      const sources = ['local', 'url', 'camera'];
      const clientAllowedFormats = [];

      if (acceptedTypes) {
        const types = acceptedTypes.split(',').map(t => t.trim());
        types.forEach(type => {
          if (type.startsWith('.')) {
            clientAllowedFormats.push(type.substring(1));
          } else if (type === 'image/*') {
            clientAllowedFormats.push('jpg', 'jpeg', 'png', 'gif', 'webp', 'svg');
          } else if (type.includes('/')) {
            const ext = type.split('/')[1];
            if (ext && ext !== '*') {
              clientAllowedFormats.push(ext);
            }
          }
        });
      }

      const widgetOptions = {
        cloudName: cloudName,
        uploadPreset: uploadPreset,
        sources: sources,
        maxFileSize: maxSize * 1024 * 1024, // Convert MB to bytes
        multiple: false,
        resourceType: 'auto',
        showAdvancedOptions: false,
        showCompletedButton: true,
        singleUploadAutoClose: true
      };

      if (clientAllowedFormats.length > 0) {
        widgetOptions.clientAllowedFormats = clientAllowedFormats;
      }

      const widget = cloudinary.createUploadWidget(widgetOptions, (error, result) => {
        if (error) {
          showError('Upload failed: ' + error.message);
          return;
        }

        if (result.event === 'success') {
          const info = result.info;
          const fileName = info.original_filename + '.' + info.format;
          setFile(fileName, info.secure_url);
        }
      });

      widget.open();
    }

    fileBtn.addEventListener('click', (e) => {
      // Don't trigger if clicking the remove button
      if (e.target.closest('[data-file-remove]')) return;

      // If Cloudinary isn't ready yet, wait and retry
      if (typeof cloudinary === 'undefined' || !cloudinary.createUploadWidget) {
        hideError();
        fileStatus.textContent = 'Loading...';

        let attempts = 0;
        const maxAttempts = 20;
        const checkInterval = setInterval(() => {
          attempts++;
          if (typeof cloudinary !== 'undefined' && cloudinary.createUploadWidget) {
            clearInterval(checkInterval);
            fileStatus.textContent = block.querySelector('.contact-form__file-btn') ? 'No file selected' : '';
            openCloudinaryWidget();
          } else if (attempts >= maxAttempts) {
            clearInterval(checkInterval);
            fileStatus.textContent = block.querySelector('.contact-form__file-btn') ? 'No file selected' : '';
            showError('Cloudinary widget failed to load. Please refresh the page and try again.');
          }
        }, 250);
        return;
      }

      openCloudinaryWidget();
    });

    fileRemove.addEventListener('click', (e) => {
      e.stopPropagation();
      clearFile();
    });
  }

  // ===========================
  // Form Submission Handling
  // ===========================
  function initFormSubmission(container) {
    const form = container.querySelector('.contact-form__form');
    if (!form) return;

    const submitBtn = form.querySelector('[data-contact-submit]');
    if (!submitBtn || initializedForms.has(form)) return;
    initializedForms.add(form);

    form.addEventListener('submit', (e) => {
      // Add loading state to button
      submitBtn.classList.add('is-loading');
      submitBtn.disabled = true;

      // Update combined phone values before submission
      const phoneBlocks = container.querySelectorAll('[data-phone-block]');
      phoneBlocks.forEach(block => {
        const phoneInput = block.querySelector('[data-phone-input]');
        const combinedInput = block.querySelector('[data-phone-combined]');
        const dialSpan = block.querySelector('[data-phone-dial]');

        if (phoneInput && combinedInput && dialSpan) {
          const phoneValue = phoneInput.value.trim();
          if (phoneValue) {
            combinedInput.value = dialSpan.textContent + ' ' + phoneValue;
          }
        }
      });

      // Let form submit naturally
      // Loading state will persist through page reload
    });
  }

  // One delegated listener avoids retaining phone blocks removed in the editor.
  document.addEventListener('click', (event) => {
    document.querySelectorAll('[data-phone-block]').forEach((block) => {
      if (block.contains(event.target)) return;
      block.querySelector('[data-phone-dropdown]')?.classList.remove('is-open');
      block.querySelector('[data-phone-country-btn]')?.setAttribute('aria-expanded', 'false');
    });
  });

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initContactForms);
  } else {
    initContactForms();
  }

  // Re-initialize in Shopify editor
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      const section = e.target;
      if (section.querySelector('[data-contact-form]')) {
        initForm(section.querySelector('[data-contact-form]'));
      }
    });

    // Watch for DOM changes to catch when blocks are replaced
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              if (node.matches('[data-contact-form]')) initForm(node);
              node.querySelectorAll('[data-contact-form]').forEach(initForm);
              if (node.matches('[data-phone-block]')) initPhoneBlock(node);
              node.querySelectorAll('[data-phone-block]').forEach(initPhoneBlock);
              if (node.matches('[data-file-block]')) initFileBlock(node);
              node.querySelectorAll('[data-file-block]').forEach(initFileBlock);
            }
          });
        }
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
